/**
 * Types for Model Results page - mirrors backend types from app/functions/rapid/schemas.ts
 */

export enum ModelChangeTrigger {
    CONTINUOUS_IMPROVEMENT = "continuous_improvement",
    MANUAL_RETRAIN = "manual_retrain",
    INITIAL_TRAINING = "initial_training"
}

export enum ModelArchitecture {
    RF_DETR = "rf_detr",
    RF_INSTANT = "rf_instant",
    SAM3 = "sam3"
}

export interface ModelReference {
    architecture: ModelArchitecture;
    variant?: string;
    ref?: string;
    prompts?: string[];
}

export interface FirebaseTimestamp {
    // Firebase Admin SDK format (used in some contexts)
    _seconds?: number;
    _nanoseconds?: number;
    // Firebase Client SDK format (used in Firestore responses)
    seconds?: number;
    nanoseconds?: number;
}

export interface ModelChange {
    id: string;
    timestamp: FirebaseTimestamp;
    from: ModelReference | null;
    to: ModelReference;
    trigger: ModelChangeTrigger;
    imageCountAtTraining: number;
    classes: string[];
    seenBy: string[];
}

export enum ModelPageState {
    // User hasn't seen latest model change - show comparison modal
    NEW_MODEL_COMPARISON = "new_model_comparison",
    // User selected a historical model to compare
    HISTORICAL_COMPARISON = "historical_comparison",
    // Single model performance view with test images (also used after marking change as seen)
    SINGLE_MODEL_PERFORMANCE = "single_model_performance",
    // Loading state
    LOADING = "loading",
    // Error state
    ERROR = "error"
}

/**
 * Widget-level state for the MetricsSnippet when embedded outside the Model page.
 * Unlike ModelPageState (which tracks the Model page's internal UI state and gets
 * mutated by page interactions), this is derived purely from the underlying data
 * (modelChanges, seenBy, training status) so it remains stable regardless of
 * Model page navigation.
 */
export enum ModelWidgetState {
    // Model is currently training
    TRAINING = "training",
    // Latest model change hasn't been seen by the user
    UNSEEN_CHANGE = "unseen_change",
    // Default state - model exists, user has seen latest change
    DEFAULT = "default"
}

/**
 * Model evaluation metrics, preferring test-set metrics (modelEvalResults)
 * over validation metrics (train.results) when available.
 */
export interface ModelMetrics {
    /** Mean Average Precision at 50% IoU (0-100) */
    map?: number;
    /** Precision (0-100) */
    precision?: number;
    /** Recall (0-100) */
    recall?: number;
    /** F1 score (0-100) */
    f1?: number;
    /** Accuracy for classification models (0-100) */
    accuracy?: number;
    /** Mean IoU for semantic segmentation (0-100) */
    miou?: number;
}

/**
 * Per-class metrics, preferring test split over validation split
 */
export interface ClassMetrics {
    /** Class name (e.g., "dog", "person") */
    className: string;
    /** Mean Average Precision at 50% IoU (0-100) */
    map?: number;
    /** Precision (0-100) */
    precision?: number;
    /** Recall (0-100) */
    recall?: number;
}

/**
 * Response from the version metrics API
 */
export interface VersionMetricsResponse {
    metrics: ModelMetrics | null;
    classMetrics: ClassMetrics[] | null;
    versionId: string;
    datasetUrl: string;
}

// ---------------------------------
// Model Comparison Types
// ---------------------------------

/**
 * Source from the test split for model comparison
 */
export interface TestSource {
    id: string;
    owner: string;
    name: string;
    width?: number;
    height?: number;
    imageUrl: string;
    thumbUrl: string;
}

/**
 * Response from the test sources API (simple, no inference)
 */
export interface TestSourcesResponse {
    sources: TestSource[];
}

/**
 * Inference result for a source/model combination (stored in frontend state)
 */
export interface SourceInferenceResult {
    modelRef: string;
    result: any; // Model inference result
    loading?: boolean;
    error?: string;
}

/**
 * Test source with inference results populated by frontend
 */
export interface TestSourceWithInference extends TestSource {
    inferences: Record<string, SourceInferenceResult>;
}
